<template>
  <div class="general-filters mb-4">
    <h3>General filters</h3>
    <div class="form-row">
      <!-- Version filter -->
      <div class="form-group col-md-6">
        <label for="versions">Version numbers</label>
        <!-- Affichage des versions sélectionnées -->
        <div v-if="form.versions.length > 0" class="mb-2">
          <ul class="list-inline d-inline-block mb-2">
            <li
              v-for="version in form.versions"
              :key="version"
              class="list-inline-item"
            >
              <b-form-tag
                @remove="removeVersion(version)"
                :title="getVersionText(version)"
                variant="primary"
                pill
                >{{ getVersionText(version) }}</b-form-tag
              >
            </li>
          </ul>
        </div>

        <!-- Select pour ajouter de nouvelles versions -->
        <b-form-select
          v-model="selectedVersion"
          :options="availableVersionOptions"
          :disabled="versionsLoading || availableVersionOptions.length === 0"
          :placeholder="
            versionsLoading ? 'Loading versions...' : 'Select a version...'
          "
          @change="addVersion"
          class="mb-2"
        >
          <template #first>
            <option disabled value="">Select a version...</option>
          </template>
        </b-form-select>
        <small class="form-text text-muted">
          <b-spinner small v-if="versionsLoading" class="mr-1"></b-spinner>
          {{
            versionsLoading
              ? 'Loading versions...'
              : 'Leave empty to display all versions'
          }}
        </small>
      </div>

      <!-- Language filter (placeholder for future) -->
      <div class="form-group col-md-6">
        <label for="language">Language</label>
        <b-form-select
          id="language"
          v-model="form.language"
          :options="languageOptions"
          :disabled="!canChangeLanguage"
          placeholder="Select language..."
        >
          <template #first>
            <option value="">Select language...</option>
          </template>
        </b-form-select>
      </div>
    </div>

    <!-- Actions -->
    <div class="form-row">
      <div class="form-group col-12">
        <div class="d-flex justify-content-end">
          <b-button variant="success" @click="applyFilters" class="mr-2">
            Apply filters
          </b-button>
          <b-button variant="outline-secondary" @click="resetFilters">
            Reset
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import versionService from '@/services/versionService'
  import { getLanguageSelectOptions } from '@/constants/languages'

  export default {
    name: 'GeneralFilters',
    props: {
      filters: {
        type: Object,
        default: () => ({
          versions: [],
          language: 'fr',
        }),
      },
      canChangeLanguage: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        form: {
          versions: [],
          language: 'fr', // Sera mis à jour dans mounted()
        },
        versions: [],
        versionsLoading: false,
        selectedVersion: null, // Version sélectionnée dans le select
      }
    },
    computed: {
      versionOptions() {
        if (this.versions.length === 0) {
          return []
        }
        return this.versions.map((version) => ({
          value: version.numberVersion,
          text: `${version.version}`,
          variant: 'primary',
        }))
      },
      availableVersionOptions() {
        // Filtrer les versions déjà sélectionnées
        const selectedVersions = this.form.versions
          .map((version) => {
            // Si c'est déjà un nombre, on l'utilise
            if (typeof version === 'number') {
              return version
            }
            // Sinon, on essaie de le convertir
            return parseInt(version, 10)
          })
          .filter((version) => !isNaN(version))

        // Retourner les options avec le numéro de version comme valeur
        return this.versions
          .filter(
            (version) => !selectedVersions.includes(version.numberVersion)
          )
          .map((version) => ({
            value: version.numberVersion,
            text: `${version.version}`,
          }))
      },
      languageOptions() {
        return getLanguageSelectOptions('en')
      },
    },
    watch: {
      filters: {
        handler(newFilters) {
          if (newFilters) {
            this.initForm()
          }
        },
        deep: true,
        immediate: true,
      },
    },
    mounted() {
      this.fetchVersions()
    },
    methods: {
      async fetchVersions() {
        this.versionsLoading = true
        try {
          const response = await versionService.getVersions({
            type: 'data',
            language: this.filters?.language || null,
            sort: 'numberVersion',
            order: 'desc',
          })
          this.versions = response
        } catch (error) {
          console.error('Erreur lors de la récupération des versions:', error)
          this.$notify({
            type: 'error',
            title: 'Erreur',
            text: 'Impossible de récupérer la liste des versions',
          })
          // En cas d'erreur, on initialise avec un tableau vide
          this.versions = []
        } finally {
          this.versionsLoading = false
        }
      },
      applyFilters() {
        // Avec b-form-tags, les versions sont stockées directement comme des chaînes
        // Nous devons les convertir en numéros pour l'API
        const versionValues = this.form.versions
          .map((version) => {
            // Si c'est déjà un nombre, on l'utilise
            if (typeof version === 'number') {
              return version
            }
            // Sinon, on essaie de le convertir
            return parseInt(version, 10)
          })
          .filter((version) => !isNaN(version)) // Filtrer les valeurs invalides

        const filters = {
          ...this.form,
          versions: versionValues,
        }
        this.$emit('apply', filters)
      },
      resetFilters() {
        this.$emit('reset')
        this.initForm()
        this.selectedVersion = null
      },
      initForm() {
        this.form = {
          versions: this.filters?.versions || [],
          language: this.filters?.language || 'fr',
        }
        this.selectedVersion = null
      },
      getVersionText(versionNumber) {
        // Trouver la version correspondante dans la liste
        const version = this.versions.find(
          (v) => v.numberVersion === versionNumber
        )
        return version ? `${version.version}` : `${versionNumber}`
      },
      addVersion() {
        if (
          this.selectedVersion &&
          !this.form.versions.includes(this.selectedVersion)
        ) {
          this.form.versions.push(this.selectedVersion)
          this.selectedVersion = null // Réinitialiser le select
        }
      },
      removeVersion(versionToRemove) {
        this.form.versions = this.form.versions.filter(
          (version) => version !== versionToRemove
        )
      },
    },
  }
</script>

<style scoped>
  .general-filters {
    background-color: #f8f9fa;
    padding: 1.5rem;
    border-radius: 0.375rem;
    border: 1px solid #dee2e6;
  }
</style>
